import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import type { Bounds, Marker } from "@repo/ui";
import { dateOf, dateTimeOf, timeOf } from "@repo/helpers";
import { MapChart } from "@repo/ui";

import type { idType } from "../../helpers/segmentFilterHook";
import { BreadcrumbsController } from "../../api/breadcrumbsController";
import { MapMarkerType, PositionViewModel } from "../../models/location";
import { MapType, useMapFilters } from "./filters/filterContext";
import { useExtractStateCodesFromLeads } from "./utils";

async function getRetailLocations(stateIds: idType[], stateCodes: number[], lastPositions: string) {
	if (!stateIds.length) return await BreadcrumbsController.getRetailLocations(stateCodes, lastPositions);
	else return await BreadcrumbsController.getRetailLocations(stateIds, lastPositions);
}

function repOrMachine(type: MapType) {
	if (type === "Representatives") return true;
	if (type === "Machines") return true;
	return false;
}

function repMachineOrEnrollment(type: MapType) {
	if (repOrMachine(type)) return true;
	if (type === "Enrollment") return true;
	return false;
}

export function Map() {
	const [bounds, setBounds] = useState<Bounds | null>(null);
	const extractStateCodesFromLeads = useExtractStateCodesFromLeads();
	const { filters } = useMapFilters();
	const filtersForAll = {
		...filters,
		limitToLastHours: null,
		poi: null,
		leads: null,
		convertedLeads: null,
		positions: null,
		showLines: null,
		showSales: null,
		goodSales: null,
		retailLocations: null,
		phoneCarrierType: null,
	};

	const liveViewFilters = { ...filtersForAll, limitToLastHours: filters.limitToLastHours };
	const liveViewQuery = useQuery({
		queryKey: ["liveView", liveViewFilters],
		queryFn: () => BreadcrumbsController.getLiveView(liveViewFilters),
		enabled: filters.type === "LiveView",
	});

	const geographicViewFilters = { ...filtersForAll };
	const geographicViewQuery = useQuery({
		queryKey: ["geographicView", bounds, geographicViewFilters],
		queryFn: () => BreadcrumbsController.getGeographicView(bounds, geographicViewFilters),
		enabled: filters.type === "GeographicView" && bounds != null,
	});

	const positionFilters = {
		...filtersForAll,
		positions: filters.positions,
		phoneCarrierType: filters.phoneCarrierType,
	};
	const positionsQuery = useQuery({
		queryKey: ["positions", positionFilters],
		queryFn: () => BreadcrumbsController.getPositions(positionFilters),
		enabled: !!filters.positions && repOrMachine(filters.type),
	});

	const salesFilters = {
		...positionFilters,
		goodSales: filters.goodSales,
		phoneCarrierType: filters.phoneCarrierType,
		showSales: filters.showSales,
	};
	const salesQuery = useQuery({
		queryKey: ["sales", salesFilters],
		queryFn: () => BreadcrumbsController.getSales(salesFilters),
		enabled: !!filters.showSales && repMachineOrEnrollment(filters.type),
	});

	const leadFilters = { ...filtersForAll, leads: filters.leads, convertedLeads: filters.convertedLeads };
	const leadsQuery = useQuery({
		queryKey: ["leads", leadFilters],
		queryFn: () => BreadcrumbsController.getLeads(leadFilters),
		enabled: !!filters.leads && repOrMachine(filters.type),
	});

	const stateCodes = useMemo(
		() => extractStateCodesFromLeads(leadsQuery.data ?? []),
		[extractStateCodesFromLeads, leadsQuery.data],
	);

	const retailLocationsQuery = useQuery({
		queryKey: ["retailLocations", filters.stateIds, stateCodes, filters.retailLocations, filters.lastPositions],
		queryFn: () => getRetailLocations(filters.stateIds, stateCodes, filters.lastPositions),
		enabled: !!filters.retailLocations,
	});

	const markers = useMemo(() => {
		const liveView = liveViewQuery.data ?? [];
		const geographicView = geographicViewQuery.data ?? [];
		const positions = positionsQuery.data ?? [];
		const sales = salesQuery.data ?? [];
		const leads = leadsQuery.data ?? [];
		const retailLocations = retailLocationsQuery.data ?? [];

		const singleLiveView = new Set(liveView.map((l) => l.key)).size === 1;
		const singleGeographicView = new Set(geographicView.map((l) => l.key)).size === 1;

		return [
			...liveView.map(MarkerMap.position(singleLiveView)),
			...geographicView.map(MarkerMap.position(singleGeographicView)),
			...positions.map(MarkerMap.position(false)),
			...sales.map(MarkerMap.position(false)),
			...leads.map(MarkerMap.position(false)),
			...retailLocations.map(MarkerMap.retailLocation),
		];
	}, [
		liveViewQuery.data,
		geographicViewQuery.data,
		positionsQuery.data,
		salesQuery.data,
		leadsQuery.data,
		retailLocationsQuery.data,
	]);

	const lineMarkers = useMemo(() => {
		const positions = positionsQuery.data ?? [];

		return [...positions.map(MarkerMap.position(false))];
	}, [positionsQuery.data]);

	const loading = useMemo(() => {
		return (
			liveViewQuery.isLoading ||
			geographicViewQuery.isLoading ||
			positionsQuery.isLoading ||
			leadsQuery.isLoading ||
			retailLocationsQuery.isLoading
		);
	}, [
		liveViewQuery.isLoading,
		geographicViewQuery.isLoading,
		positionsQuery.isLoading,
		leadsQuery.isLoading,
		retailLocationsQuery.isLoading,
	]);

	const onBoundsChange = useCallback((bounds: Bounds) => {
		setBounds(bounds);
	}, []);

	return (
		<section>
			<MapChart
				data={markers}
				type="map"
				mapType="roadmap"
				id={""}
				title={""}
				height="88vh"
				loading={loading}
				onBoundsChange={onBoundsChange}
				dontUpdateBounds={filters.type === "GeographicView"}
				forcedCenter={filters.poi}
				lineData={lineMarkers}
				showLines={filters.showLines}
			/>
		</section>
	);
}

export class MarkerMap {
	private static handleTooltip(item: string, date: Date) {
		const parts = item.split(" - ");

		const newParts = parts.map((part) => this.handleDateTime(part, date));

		return newParts.join(" - ");
	}

	private static handleDateTime(item: string, date: Date) {
		if (item === "DATE_TIME") return dateTimeOf(date);
		if (item === "DATE") return dateOf(date);
		if (item === "TIME") return timeOf(date);

		return item;
	}

	public static position = (single: boolean) => (item: PositionViewModel) => {
		return {
			id: item.id,
			datetime: new Date(item.date),
			lat: item.lat,
			lon: item.lng,
			label: Object.keys(item.displayFields)
				.filter((k) => item.displayFields[k] != null)
				.map((k) => ({ [k]: this.handleDateTime(item.displayFields[k], new Date(item.date)) })),
			tooltip: this.handleTooltip(item.tooltip, new Date(item.date)),
			image:
				item.markerType === MapMarkerType.Flag
					? "/Content/Map/map-flag-black.png"
					: "/Content/Map/map-pin-black.png",
			group: single ? item.date : item.key,
			links: item.links,
		} as Marker;
	};

	public static retailLocation = (item: PositionViewModel) => {
		return {
			id: item.id,
			datetime: new Date(item.date),
			lat: item.lat,
			lon: item.lng,
			label: Object.keys(item.displayFields)
				.filter((k) => item.displayFields[k] != null)
				.map((k) => ({ [k]: this.handleDateTime(item.displayFields[k], new Date(item.date)) })),
			tooltip: this.handleTooltip(item.tooltip, new Date(item.date)),
			image: "/Content/Map/map-pin-blue.png",
			group: undefined,
		} as Marker;
	};
}
